import type { ApiClient } from '@carbonwebmail/api-client'
import type { ApiKeyDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { CreateCard } from '../../ui/CreateCard.js'
import { Field } from '../../ui/Field.js'
import { when } from '../../ui/format.js'
import { EmptyCard, ListScreen } from '../../ui/PagedScreen.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Td, Tr } from '../../ui/Table.js'

import { useApiKeys } from './useApiKeys.js'

/**
 * Keys that let a program act for this organisation.
 *
 * Three things are said out loud, because each is something a keys screen
 * usually leaves somebody to find out.
 *
 * **The key is shown once.** Only a fingerprint is stored, so nothing here can
 * produce it again — which is what makes the stored row harmless to read. The
 * banner says so while the key is on screen, not afterwards.
 *
 * **A key can do the work and not change the organisation.** It acts as a
 * member: contacts, segments, templates, campaigns, sends. It cannot add
 * people, change where mail comes from, or mint another key. A leaked key is a
 * nuisance rather than a takeover, and somebody deciding where to put one
 * deserves to know that before they do.
 *
 * **A revoked key stays on the list.** The question asked after a leak is "was
 * this ever live, and when was it last used" — and a row that vanished takes
 * the answer with it.
 */
export function ApiKeysPage({ client }: { readonly client: ApiClient }) {
  const keys = useApiKeys({ client })
  const { state, minted, problem, busy } = keys

  const [label, setLabel] = useState('')

  const create = useCallback(async () => {
    if (await keys.create(label)) setLabel('')
  }, [keys, label])

  return (
    <>
      <header className="mb-6">
        <p className="mt-1 text-[13px] text-t2">
          For a program acting on your behalf. A key can read and write contacts, segments,
          templates and campaigns, and can send — it cannot change your team, your settings, or your
          sending domains.
        </p>
      </header>

      {problem === undefined ? null : <Alert>{problem}</Alert>}

      {minted === undefined ? null : (
        <div className="mb-6 rounded-card border border-brand bg-brand-soft p-5">
          <h2 className="font-bold text-[15px] text-t1">Copy this now</h2>
          <p className="mt-1 text-[13px] text-t2">
            This is the only time it will be shown. We store a fingerprint, not the key, so we
            cannot show it to you again — if you lose it, revoke it and make another.
          </p>
          <code className="mt-3 block overflow-x-auto rounded-field border border-line bg-surface px-3 py-2.5 font-mono text-[13px] text-t1">
            {minted}
          </code>
          <button
            type="button"
            onClick={keys.forget}
            className="mt-3 font-semibold text-[13px] text-brand underline"
          >
            I have copied it
          </button>
        </div>
      )}

      <CreateCard
        submit="Create a key"
        disabled={busy || label.trim() === ''}
        onSubmit={() => void create()}
      >
        <Field
          id="key-label"
          label="What is it for"
          type="text"
          autoComplete="off"
          value={label}
          disabled={busy}
          hint="A name you will recognise later — “nightly contact sync”, not “key 2”."
          onChange={setLabel}
        />
      </CreateCard>

      <ListScreen
        state={state}
        onRetry={() => void keys.retry()}
        head={['Name', 'Key', 'Last used', 'Created', '']}
        minWidth={840}
        empty={
          <EmptyCard heading="No keys yet">
            Create one when you have something that needs to reach this organisation without a
            person signing in.
          </EmptyCard>
        }
        row={(key) => <KeyRow key={key.id} apiKey={key} busy={busy} onRevoke={keys.revoke} />}
      />
    </>
  )
}

function KeyRow({
  apiKey,
  busy,
  onRevoke,
}: {
  readonly apiKey: ApiKeyDto
  readonly busy: boolean
  readonly onRevoke: (id: string) => Promise<void>
}) {
  const revoked = apiKey.revokedAt !== null

  return (
    <Tr>
      <Td strong>
        {apiKey.label}
        {revoked ? (
          <span className="ml-2">
            <StatusPill tone="danger">revoked</StatusPill>
          </span>
        ) : null}
      </Td>
      {/* The visible half, which identifies the row and opens nothing. It is
          what lets somebody match a key in a log to a key on this screen. */}
      <Td>
        <span className="font-mono text-[12px] text-t2">cwm_{apiKey.prefix}…</span>
      </Td>
      {/* Never used is its own answer, and the one that makes a key safe to
          revoke without wondering what will break. */}
      <Td>{apiKey.lastUsedAt === null ? 'never' : when(apiKey.lastUsedAt)}</Td>
      <Td>{when(apiKey.createdAt)}</Td>
      <Td>
        {revoked ? null : (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onRevoke(apiKey.id)}
            className="font-medium text-[13px] text-t3 hover:text-danger disabled:opacity-60"
          >
            Revoke
          </button>
        )}
      </Td>
    </Tr>
  )
}
